<script lang="ts" setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { watchDebounced } from "@vueuse/core";

import Checkbox from "@/components/px-checkbox/px-checkbox.vue";
import RangeSelector from "./range-selector.vue";
import FacetSearchDropdown from "./facet-search-dropdown.vue";
import { EMatchingSearchActions } from "@/modules/matching/services/store/matching-search/matching-search.types";

const store = useStore();

const vilLevel = computed(() => store.state.matchingSearch.filters.vil);
const sortOrder = computed(() => store.state.matchingSearch.filters.sort);
const searchQuery = computed(() => store.state.matchingSearch.filters.query);
const loadingFacet = computed(() => store.state.matchingSearch.loadingFacet);

const foundingDateFacetQuery = computed(
  () => store.state.matchingSearch.filters.foundingDateFacetQuery,
);
const sectorsFacetQuery = computed(
  () => store.state.matchingSearch.filters.sectorsFacetQuery,
);
const countriesFacetQuery = computed(
  () => store.state.matchingSearch.filters.countriesFacetQuery,
);

// Prepare Facet Filter Options
const sectors = computed(() => {
  return store.state.matchingSearch.facets?.sectors?.options.map(
    (option: any) => {
      return {
        component: Checkbox,
        props: {
          key: option.value,
          value: option.value,
          label: `${option.label} (${option.count})`,
          checked: store.state.matchingSearch.filters.sectors.includes(
            option.value,
          ),
        },
        listeners: {
          change: () => {
            store.dispatch(EMatchingSearchActions.SET_FILTERS, {
              filter: "sectors",
              action: "update",
              value: option.value,
            });
          },
        },
      };
    },
  );
});
const countries = computed(() => {
  return store.state.matchingSearch.facets?.countries?.options.map(
    (option: any) => ({
      component: Checkbox,
      props: {
        key: option.value,
        label: `${option.label} (${option.count})`,
        value: option.value,
        checked: store.state.matchingSearch.filters.countries.includes(
          option.value,
        ),
      },
      listeners: {
        change: () => {
          store.dispatch(EMatchingSearchActions.SET_FILTERS, {
            filter: "countries",
            action: "update",
            value: option.value,
          });
        },
      },
    }),
  );
});
const foundingDates = computed(() => {
  return store.state.matchingSearch.facets?.founding_date?.options.map(
    (option: any) => ({
      component: Checkbox,
      props: {
        key: option.value,
        value: option.value,
        label: `${option.label === "0" ? "No Founding Date" : option.label} (${option.count})`,
        checked: store.state.matchingSearch.filters.founding_date.includes(
          option.value,
        ),
      },
      listeners: {
        change: () => {
          store.dispatch(EMatchingSearchActions.SET_FILTERS, {
            filter: "founding_date",
            action: "update",
            value: option.value,
          });
        },
      },
    }),
  );
});

// Update Facet Filter Selections
const updateSectorsQuery = (value: string) => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "sectorsFacetQuery",
    action: "update",
    value: value,
  });
};
const updateCountriesQuery = (value: string) => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "countriesFacetQuery",
    action: "update",
    value: value,
  });
};
const updateFoundingDateQuery = (value: string) => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "foundingDateFacetQuery",
    action: "update",
    value: value,
  });
};
const updateSearchQuery = (value: string) => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "query",
    action: "update",
    value: value,
  });
};
const updateSortOrder = (order: string) => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "sort",
    action: "update",
    value: order,
  });
};
const updateLevel = (value: number[]) => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "vil",
    action: "update",
    value: value,
  });
};

// Clear Facet Filters
const clearSectorsFacet = () => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "sectors",
    action: "clear",
    value: [],
  });
};
const clearCountriesFacet = () => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "countries",
    action: "clear",
    value: [],
  });
};
const clearFoundingDateFacet = () => {
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "founding_date",
    action: "clear",
    value: [],
  });
};
const clearAllFilters = () => {
  console.log("triggered");
  store.dispatch(EMatchingSearchActions.SET_FILTERS, {
    filter: "all",
    action: "clear",
  });
};

watchDebounced(
  sectorsFacetQuery,
  () => {
    store.dispatch(EMatchingSearchActions.SEARCH_FILTERS, {
      facet: "sectors",
      query: sectorsFacetQuery.value,
    });
  },
  { debounce: 500, maxWait: 1000 },
);
watchDebounced(
  countriesFacetQuery,
  () => {
    store.dispatch(EMatchingSearchActions.SEARCH_FILTERS, {
      facet: "countries",
      query: countriesFacetQuery.value,
    });
  },
  { debounce: 500, maxWait: 1000 },
);
watchDebounced(
  foundingDateFacetQuery,
  () => {
    store.dispatch(EMatchingSearchActions.SEARCH_FILTERS, {
      facet: "founding_date",
      query: foundingDateFacetQuery.value,
    });
  },
  { debounce: 500, maxWait: 1000 },
);
watchDebounced(
  searchQuery,
  () => {
    store.dispatch(EMatchingSearchActions.SEARCH, "search");
  },
  { debounce: 500 },
);
</script>

<template>
  <div class="filter-bar">
    <div class="filter-bar__left">
      <span class="title">Filters</span>
      <div class="dropdowns">
        <RangeSelector :value="vilLevel" @update-range="updateLevel" />
        <FacetSearchDropdown
          title="HQ Country"
          :options="countries"
          :loading="loadingFacet"
          :has-search="true"
          :query="countriesFacetQuery"
          search-placeholder="Search Countries"
          @update-query="updateCountriesQuery"
          @clear-all-filters="clearCountriesFacet"
        />
        <FacetSearchDropdown
          title="Sectors"
          :options="sectors"
          :loading="loadingFacet"
          :has-search="true"
          :query="sectorsFacetQuery"
          search-placeholder="Search Sectors"
          @update-query="updateSectorsQuery"
          @clear-all-filters="clearSectorsFacet"
        />
        <FacetSearchDropdown
          :options="foundingDates"
          title="Founded Date"
          :loading="loadingFacet"
          :has-search="true"
          :query="foundingDateFacetQuery"
          search-placeholder="Search Year"
          @update-query="updateFoundingDateQuery"
          @clear-all-filters="clearFoundingDateFacet"
        />
        <PxButton size="small" @click="clearAllFilters">
          Clear All Filters
        </PxButton>
      </div>
    </div>
    <div class="search">
      <ElInput
        clearable
        :model-value="searchQuery"
        placeholder="Search name, sectors, countries, regions, cities"
        @input="updateSearchQuery"
      >
        <template #prefix>
          <PxIcon :size="24" name="magnifier" />
        </template>
      </ElInput>
      <ElDropdown placement="bottom-start">
        <ElTag type="primary" effect="plain">
          Sort By ({{ sortOrder === "desc" ? "Latest" : "Oldest" }})
        </ElTag>
        <template #dropdown>
          <ElDropdownMenu>
            <ElDropdownItem @click="updateSortOrder('desc')">
              Latest Assessment First
            </ElDropdownItem>
            <ElDropdownItem @click="updateSortOrder('asc')">
              Oldest Assessment First
            </ElDropdownItem>
          </ElDropdownMenu>
        </template>
      </ElDropdown>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.filter-bar {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  &__left {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: start;
  }

  .title {
    line-height: 16px;
    font-size: 0.86rem;
    font-weight: 600;
    color: #8b8fa1;
    margin-right: 16px;
  }

  .dropdowns {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
  }

  .search {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;

    .el-dropdown .el-tag {
      padding: 10px;
      border-left: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}
</style>
